import React, { useEffect, useState, Component } from 'react';
import { Link } from 'react-router-dom';
import { motion, useMotionValue, useTransform, useSpring } from 'framer-motion';
import { toast } from 'sonner';
import { WalletIcon, PlaneIcon, PlusIcon, ArrowUpRightIcon, SparklesIcon, TrendingUpIcon, CreditCardIcon, ArrowDownIcon, TagIcon, CheckCircleIcon } from 'lucide-react';
import { ROUTES } from '../constants/routes';
import { useWallet } from '../hooks/useWallet';
import { useWithdrawals } from '../hooks/useWithdrawals';
import { usePromoCodes } from '../hooks/usePromoCodes';
import { useNotifications } from '../hooks/useNotifications';
import { formatNaira } from '../utils/formatCurrency';
import { NIGERIAN_BANKS } from '../constants/data';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal, ModalFooter } from '../components/ui/Modal';
import { TransactionList } from '../components/features/TransactionList';
// Animated Counter
function AnimatedBalance({
  value
}: {
  value: number;
}) {
  const [displayValue, setDisplayValue] = useState(0);
  useEffect(() => {
    const duration = 1500;
    const startTime = Date.now();
    const startValue = displayValue;
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 4);
      setDisplayValue(Math.floor(startValue + (value - startValue) * easeOut));
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [value]);
  return <span className="tabular-nums" data-id="element-1775">{formatNaira(displayValue)}</span>;
}
// 3D Card Component
function WalletCard({
  balance,
  onWithdraw
}: {
  balance: number;
  onWithdraw: () => void;
}) {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useTransform(y, [-100, 100], [10, -10]);
  const rotateY = useTransform(x, [-100, 100], [-10, 10]);
  const springConfig = {
    stiffness: 300,
    damping: 30
  };
  const rotateXSpring = useSpring(rotateX, springConfig);
  const rotateYSpring = useSpring(rotateY, springConfig);
  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    x.set(event.clientX - centerX);
    y.set(event.clientY - centerY);
  };
  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };
  return <motion.div className="perspective-1000" style={{
    perspective: 1000
  }} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} data-id="element-1776">
      <motion.div style={{
      rotateX: rotateXSpring,
      rotateY: rotateYSpring,
      transformStyle: 'preserve-3d'
    }} data-id="element-1777">
        <Card variant="elevated" padding="none" className="overflow-hidden bg-gradient-to-br from-primary-500 via-primary-600 to-primary-700 border-0 shadow-2xl shadow-primary-500/30" data-id="element-1778">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10" data-id="element-1779">
            <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '32px 32px'
          }} data-id="element-1780" />
          </div>

          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-1781" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-accent-500/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" data-id="element-1782" />

          <div className="p-6 sm:p-8 relative" data-id="element-1783">
            <div className="flex items-start justify-between mb-8" data-id="element-1784">
              <div data-id="element-1785">
                <div className="flex items-center gap-2 mb-2" data-id="element-1786">
                  <CreditCardIcon className="w-5 h-5 text-white/70" data-id="element-1787" />
                  <p className="text-body-md text-white/80" data-id="element-1788">
                    Store Credit Balance
                  </p>
                </div>
                <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.2
              }} data-id="element-1789">
                  <p className="text-display-lg text-white font-bold" data-id="element-1790">
                    <AnimatedBalance value={balance} data-id="element-1791" />
                  </p>
                </motion.div>
              </div>
              <motion.div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm" whileHover={{
              scale: 1.1,
              rotate: 10
            }} data-id="element-1792">
                <WalletIcon className="w-7 h-7 text-white" data-id="element-1793" />
              </motion.div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-3 gap-3" data-id="element-1794">
              <motion.div whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-1795">
                <Link to={ROUTES.SEARCH} data-id="element-1796">
                  <Button fullWidth className="bg-white text-primary-600 hover:bg-white/90 shadow-lg" icon={<PlaneIcon className="w-4 h-4" data-id="element-1798" />} data-id="element-1797">
                    Book
                  </Button>
                </Link>
              </motion.div>
              <motion.div whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-1799">
                <Button fullWidth variant="ghost" className="text-white border-white/30 hover:bg-white/10" icon={<ArrowDownIcon className="w-4 h-4" data-id="element-1801" />} onClick={onWithdraw} data-id="element-1800">
                  Withdraw
                </Button>
              </motion.div>
              <motion.div whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-1802">
                <Link to={ROUTES.REFUND} data-id="element-1803">
                  <Button fullWidth variant="ghost" className="text-white border-white/30 hover:bg-white/10" icon={<PlusIcon className="w-4 h-4" data-id="element-1805" />} data-id="element-1804">
                    Add
                  </Button>
                </Link>
              </motion.div>
            </div>
          </div>

          {/* Bonus Banner */}
          <div className="px-6 sm:px-8 py-4 bg-gradient-to-r from-accent-500/30 to-accent-600/30 border-t border-white/10 backdrop-blur-sm" data-id="element-1806">
            <div className="flex items-center gap-3 text-white" data-id="element-1807">
              <motion.div animate={{
              rotate: [0, 10, -10, 0]
            }} transition={{
              duration: 2,
              repeat: Infinity
            }} data-id="element-1808">
                <SparklesIcon className="w-5 h-5 text-accent-300" data-id="element-1809" />
              </motion.div>
              <p className="text-body-sm" data-id="element-1810">
                <strong className="text-accent-200" data-id="element-1811">10% bonus</strong> on all
                refunds converted to store credit
              </p>
            </div>
          </div>
        </Card>
      </motion.div>
    </motion.div>;
}
// Stats Card
function StatCard({
  icon: Icon,
  label,
  value,
  trend
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  trend?: string;
}) {
  return <motion.div whileHover={{
    y: -4
  }} transition={{
    duration: 0.2
  }} data-id="element-1812">
      <Card variant="flat" padding="md" className="h-full" data-id="element-1813">
        <div className="flex items-start justify-between" data-id="element-1814">
          <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center" data-id="element-1815">
            <Icon className="w-5 h-5 text-primary-600" data-id="element-1816" />
          </div>
          {trend && <span className="text-caption text-success-600 font-medium flex items-center gap-1" data-id="element-1817">
              <TrendingUpIcon className="w-3 h-3" data-id="element-1818" />
              {trend}
            </span>}
        </div>
        <p className="text-heading-md text-surface-900 mt-3 tabular-nums" data-id="element-1819">
          {value}
        </p>
        <p className="text-body-sm text-surface-500" data-id="element-1820">{label}</p>
      </Card>
    </motion.div>;
}
export function WalletPage() {
  const {
    balance,
    transactions,
    isLoading,
    getRecentTransactions,
    getTotalCredits,
    getTotalDebits,
    applyPromoCredit
  } = useWallet();
  const {
    requestWithdrawal
  } = useWithdrawals();
  const {
    validatePromoCode,
    redeemPromoCode
  } = usePromoCodes();
  const {
    createNotification
  } = useNotifications();
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [bankCode, setBankCode] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountName, setAccountName] = useState('John Doe'); // Mock
  const [promoCode, setPromoCode] = useState('');
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const recentTransactions = getRecentTransactions(5);
  const MIN_WITHDRAWAL = 5000;
  const handleWithdraw = async () => {
    const amount = parseInt(withdrawAmount);
    if (amount < MIN_WITHDRAWAL) {
      toast.error(`Minimum withdrawal amount is ${formatNaira(MIN_WITHDRAWAL)}`);
      return;
    }
    if (amount > balance) {
      toast.error('Insufficient balance');
      return;
    }
    if (!bankCode || !accountNumber) {
      toast.error('Please fill in all bank details');
      return;
    }
    setIsProcessing(true);
    try {
      await requestWithdrawal(amount, {
        bankName: NIGERIAN_BANKS.find(b => b.code === bankCode)?.name || '',
        bankCode,
        accountNumber,
        accountName
      });
      await createNotification('withdrawal_requested', 'Withdrawal Requested', `Your withdrawal of ${formatNaira(amount)} is being reviewed.`);
      toast.success('Withdrawal request submitted successfully!');
      setShowWithdrawModal(false);
      setWithdrawAmount('');
      setBankCode('');
      setAccountNumber('');
    } catch (error) {
      toast.error('Failed to submit withdrawal request');
    } finally {
      setIsProcessing(false);
    }
  };
  const handleValidatePromo = async () => {
    if (!promoCode.trim()) {
      toast.error('Please enter a promo code');
      return;
    }
    setIsProcessing(true);
    try {
      const result = await validatePromoCode(promoCode, balance);
      if (result.valid) {
        setPromoDiscount(result.discount);
        toast.success(`Valid! You'll receive ${formatNaira(result.discount)} credit`);
      } else {
        toast.error(result.error || 'Invalid promo code');
        setPromoDiscount(0);
      }
    } catch (error) {
      toast.error('Failed to validate promo code');
    } finally {
      setIsProcessing(false);
    }
  };
  const handleRedeemPromo = async () => {
    if (promoDiscount === 0) {
      toast.error('Please validate the promo code first');
      return;
    }
    setIsProcessing(true);
    try {
      await redeemPromoCode(promoCode, balance);
      await applyPromoCredit(promoDiscount, promoCode);
      await createNotification('promo_applied', 'Promo Code Applied!', `${formatNaira(promoDiscount)} has been added to your wallet.`);
      toast.success(`${formatNaira(promoDiscount)} added to your wallet!`);
      setShowPromoModal(false);
      setPromoCode('');
      setPromoDiscount(0);
    } catch (error) {
      toast.error('Failed to redeem promo code');
    } finally {
      setIsProcessing(false);
    }
  };
  return <div className="min-h-screen bg-gradient-to-b from-primary-50/50 via-white to-surface-50" data-id="element-1821">
      <Header walletBalance={balance} data-id="element-1822" />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-1823">
        {/* Balance Card */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mb-8" data-id="element-1824">
          <WalletCard balance={balance} onWithdraw={() => setShowWithdrawModal(true)} data-id="element-1825" />
        </motion.div>

        {/* Promo Code Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.05
      }} className="mb-8" data-id="element-1826">
          <Card variant="flat" padding="md" className="bg-gradient-to-r from-accent-50 to-accent-100/50 border-accent-200" data-id="element-1827">
            <div className="flex items-center justify-between" data-id="element-1828">
              <div className="flex items-center gap-3" data-id="element-1829">
                <div className="w-10 h-10 bg-accent-500 rounded-xl flex items-center justify-center" data-id="element-1830">
                  <TagIcon className="w-5 h-5 text-white" data-id="element-1831" />
                </div>
                <div data-id="element-1832">
                  <h3 className="text-body-md font-semibold text-surface-900" data-id="element-1833">
                    Have a Promo Code?
                  </h3>
                  <p className="text-body-sm text-surface-600" data-id="element-1834">
                    Redeem it to add credit to your wallet
                  </p>
                </div>
              </div>
              <Button variant="secondary" size="sm" onClick={() => setShowPromoModal(true)} icon={<SparklesIcon className="w-4 h-4" data-id="element-1836" />} data-id="element-1835">
                Redeem
              </Button>
            </div>
          </Card>
        </motion.div>

        {/* Stats Grid */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8" data-id="element-1837">
          <StatCard icon={TrendingUpIcon} label="Total Credits" value={formatNaira(getTotalCredits())} trend="+12%" data-id="element-1838" />
          <StatCard icon={PlaneIcon} label="Total Spent" value={formatNaira(getTotalDebits())} data-id="element-1839" />
          <div className="col-span-2 sm:col-span-1" data-id="element-1840">
            <StatCard icon={SparklesIcon} label="Transactions" value={transactions.length.toString()} data-id="element-1841" />
          </div>
        </motion.div>

        {/* Recent Transactions */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} data-id="element-1842">
          <Card variant="elevated" padding="none" className="shadow-xl border-0" data-id="element-1843">
            <div className="p-6 border-b border-surface-100" data-id="element-1844">
              <div className="flex items-center justify-between" data-id="element-1845">
                <div data-id="element-1846">
                  <h2 className="text-heading-lg text-surface-900" data-id="element-1847">
                    Recent Transactions
                  </h2>
                  <p className="text-body-sm text-surface-500 mt-1" data-id="element-1848">
                    Your latest wallet activity
                  </p>
                </div>
                <Link to={ROUTES.HISTORY} data-id="element-1849">
                  <motion.div whileHover={{
                  scale: 1.02
                }} whileTap={{
                  scale: 0.98
                }} data-id="element-1850">
                    <Button variant="ghost" size="sm" icon={<ArrowUpRightIcon className="w-4 h-4" data-id="element-1852" />} iconPosition="right" data-id="element-1851">
                      View All
                    </Button>
                  </motion.div>
                </Link>
              </div>
            </div>

            <div className="p-6" data-id="element-1853">
              {isLoading ? <div className="space-y-4" data-id="element-1854">
                  {Array.from({
                length: 3
              }).map((_, i) => <motion.div key={i} className="flex items-center gap-4" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                delay: i * 0.1
              }} data-id="element-1855">
                      <div className="w-12 h-12 bg-surface-200 rounded-xl animate-pulse" data-id="element-1856" />
                      <div className="flex-1" data-id="element-1857">
                        <div className="h-4 bg-surface-200 rounded w-1/2 mb-2 animate-pulse" data-id="element-1858" />
                        <div className="h-3 bg-surface-200 rounded w-1/3 animate-pulse" data-id="element-1859" />
                      </div>
                      <div className="h-4 bg-surface-200 rounded w-20 animate-pulse" data-id="element-1860" />
                    </motion.div>)}
                </div> : recentTransactions.length > 0 ? <TransactionList transactions={recentTransactions} data-id="element-1861" /> : <div className="text-center py-8" data-id="element-1862">
                  <div className="w-16 h-16 mx-auto mb-4 bg-surface-100 rounded-full flex items-center justify-center" data-id="element-1863">
                    <WalletIcon className="w-8 h-8 text-surface-400" data-id="element-1864" />
                  </div>
                  <p className="text-body-md text-surface-600" data-id="element-1865">
                    No transactions yet
                  </p>
                </div>}
            </div>
          </Card>
        </motion.div>

        {/* Empty State CTA */}
        {!isLoading && transactions.length === 0 && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="mt-8" data-id="element-1866">
            <Card variant="flat" padding="lg" className="text-center bg-gradient-to-br from-primary-50 to-accent-50 border-primary-100" data-id="element-1867">
              <motion.div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center shadow-lg shadow-primary-500/30" animate={{
            y: [0, -5, 0]
          }} transition={{
            duration: 2,
            repeat: Infinity
          }} data-id="element-1868">
                <WalletIcon className="w-10 h-10 text-white" data-id="element-1869" />
              </motion.div>
              <h3 className="text-heading-lg text-surface-900 mb-2" data-id="element-1870">
                Start Building Your Credit
              </h3>
              <p className="text-body-md text-surface-600 mb-6 max-w-md mx-auto" data-id="element-1871">
                Get a refund and choose store credit to receive a 10% bonus. Use
                it to book flights instantly.
              </p>
              <Link to={ROUTES.REFUND} data-id="element-1872">
                <motion.div whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-1873">
                  <Button icon={<PlusIcon className="w-4 h-4" data-id="element-1875" />} className="shadow-lg shadow-primary-500/20" data-id="element-1874">
                    Get a Refund
                  </Button>
                </motion.div>
              </Link>
            </Card>
          </motion.div>}
      </main>

      {/* Withdrawal Modal */}
      <Modal isOpen={showWithdrawModal} onClose={() => setShowWithdrawModal(false)} title="Withdraw to Bank Account" size="md" data-id="element-1876">
        <div className="space-y-4" data-id="element-1877">
          <div className="p-4 bg-primary-50 border border-primary-200 rounded-lg" data-id="element-1878">
            <p className="text-body-sm text-primary-700" data-id="element-1879">
              <strong data-id="element-1880">Available Balance:</strong> {formatNaira(balance)}
            </p>
            <p className="text-caption text-primary-600 mt-1" data-id="element-1881">
              Minimum withdrawal: {formatNaira(MIN_WITHDRAWAL)}
            </p>
          </div>

          <Input label="Withdrawal Amount" type="number" placeholder="Enter amount" value={withdrawAmount} onChange={e => setWithdrawAmount(e.target.value)} data-id="element-1882" />

          <Select label="Bank" placeholder="Select your bank" options={NIGERIAN_BANKS.map(bank => ({
          value: bank.code,
          label: bank.name
        }))} value={bankCode} onChange={setBankCode} data-id="element-1883" />

          <Input label="Account Number" placeholder="Enter 10-digit account number" value={accountNumber} onChange={e => setAccountNumber(e.target.value.replace(/\D/g, '').slice(0, 10))} maxLength={10} data-id="element-1884" />

          {accountName && <div className="p-3 bg-success-50 border border-success-200 rounded-lg" data-id="element-1885">
              <p className="text-body-sm text-success-700 flex items-center gap-2" data-id="element-1886">
                <CheckCircleIcon className="w-4 h-4" data-id="element-1887" />
                Account Name: <strong data-id="element-1888">{accountName}</strong>
              </p>
            </div>}
        </div>

        <ModalFooter data-id="element-1889">
          <Button variant="secondary" onClick={() => setShowWithdrawModal(false)} data-id="element-1890">
            Cancel
          </Button>
          <Button onClick={handleWithdraw} loading={isProcessing} disabled={!withdrawAmount || !bankCode || !accountNumber} data-id="element-1891">
            Request Withdrawal
          </Button>
        </ModalFooter>
      </Modal>

      {/* Promo Code Modal */}
      <Modal isOpen={showPromoModal} onClose={() => {
      setShowPromoModal(false);
      setPromoCode('');
      setPromoDiscount(0);
    }} title="Redeem Promo Code" size="sm" data-id="element-1892">
        <div className="space-y-4" data-id="element-1893">
          <div className="p-4 bg-accent-50 border border-accent-200 rounded-lg" data-id="element-1894">
            <p className="text-body-sm text-accent-700" data-id="element-1895">
              Enter your promo code to receive bonus credit in your wallet.
            </p>
          </div>

          <Input label="Promo Code" placeholder="Enter promo code" value={promoCode} onChange={e => setPromoCode(e.target.value.toUpperCase())} data-id="element-1896" />

          {promoDiscount > 0 && <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="p-4 bg-success-50 border border-success-200 rounded-lg" data-id="element-1897">
              <p className="text-body-md text-success-700 font-semibold flex items-center gap-2" data-id="element-1898">
                <CheckCircleIcon className="w-5 h-5" data-id="element-1899" />
                You'll receive {formatNaira(promoDiscount)} credit!
              </p>
            </motion.div>}
        </div>

        <ModalFooter data-id="element-1900">
          <Button variant="secondary" onClick={() => {
          setShowPromoModal(false);
          setPromoCode('');
          setPromoDiscount(0);
        }} data-id="element-1901">
            Cancel
          </Button>
          {promoDiscount === 0 ? <Button onClick={handleValidatePromo} loading={isProcessing} disabled={!promoCode.trim()} data-id="element-1902">
              Validate Code
            </Button> : <Button onClick={handleRedeemPromo} loading={isProcessing} icon={<SparklesIcon className="w-4 h-4" data-id="element-1904" />} data-id="element-1903">
              Redeem Now
            </Button>}
        </ModalFooter>
      </Modal>

      <Footer data-id="element-1905" />
    </div>;
}